/**
 * Audit Logging Service
 * Comprehensive activity tracking for all admin panel actions
 */

import { collection, addDoc, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  AuditLog,
  AuditAction,
  AuditResource,
  AuditSeverity,
  AuditMetadata,
  AuditChanges,
  AuditLogFilter,
  AuditLogStats
} from '../types/audit';

const AUDIT_COLLECTION = 'audit_logs';

class AuditService {
  /**
   * Get user metadata from browser
   */
  private getUserMetadata(): AuditMetadata {
    const userAgent = navigator.userAgent;
    
    return {
      userAgent,
      browser: this.detectBrowser(userAgent),
      os: this.detectOS(userAgent),
      deviceType: this.detectDeviceType(userAgent),
      sessionId: this.getSessionId(),
    };
  }

  /**
   * Detect browser from user agent
   */
  private detectBrowser(userAgent: string): string {
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  /**
   * Detect OS from user agent
   */
  private detectOS(userAgent: string): string {
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Mac')) return 'MacOS';
    if (userAgent.includes('Linux')) return 'Linux';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  /**
   * Detect device type from user agent
   */
  private detectDeviceType(userAgent: string): 'desktop' | 'mobile' | 'tablet' {
    if (/Mobile|Android|iPhone/i.test(userAgent)) return 'mobile';
    if (/iPad|Tablet/i.test(userAgent)) return 'tablet';
    return 'desktop';
  }

  /**
   * Get or create session ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('audit_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('audit_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Calculate changes between before and after states
   */
  private calculateChanges(before: any, after: any): AuditChanges | undefined {
    if (!before || !after) {
      return undefined;
    }

    const fields: string[] = [];
    const beforeData: any = {};
    const afterData: any = {};

    // Find changed fields, filtering out undefined values
    Object.keys(after).forEach(key => {
      const beforeValue = before[key];
      const afterValue = after[key];
      
      // Skip if both values are undefined
      if (beforeValue === undefined && afterValue === undefined) {
        return;
      }
      
      if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
        fields.push(key);
        // Only add to beforeData/afterData if not undefined
        if (beforeValue !== undefined) {
          beforeData[key] = beforeValue;
        }
        if (afterValue !== undefined) {
          afterData[key] = afterValue;
        }
      }
    });

    return {
      before: beforeData,
      after: afterData,
      fields,
    };
  }

  /**
   * Log an audit event
   */
  async log(params: {
    action: AuditAction;
    resource: AuditResource;
    resourceId: string;
    resourceName?: string;
    description: string;
    userId: string;
    userEmail: string;
    userName: string;
    userRole: string;
    changes?: { before?: any; after?: any };
    severity?: AuditSeverity;
    success?: boolean;
    errorMessage?: string;
    additionalData?: Record<string, any>;
  }): Promise<string> {
    try {
      const metadata = this.getUserMetadata();
      const changes = params.changes ? this.calculateChanges(params.changes.before, params.changes.after) : undefined;

      const auditLog: Omit<AuditLog, 'id'> = {
        timestamp: new Date(),
        userId: params.userId,
        userEmail: params.userEmail,
        userName: params.userName,
        userRole: params.userRole,
        action: params.action,
        resource: params.resource,
        resourceId: params.resourceId,
        resourceName: params.resourceName,
        description: params.description,
        changes,
        severity: params.severity || AuditSeverity.INFO,
        metadata,
        success: params.success !== undefined ? params.success : true,
        errorMessage: params.errorMessage,
        additionalData: params.additionalData,
      };

      const docRef = await addDoc(collection(db, AUDIT_COLLECTION), {
        ...auditLog,
        timestamp: Timestamp.fromDate(auditLog.timestamp),
      });

      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Log authentication event
   */
  async logAuth(params: {
    action: AuditAction;
    userEmail: string;
    userName?: string;
    userId?: string;
    success: boolean;
    errorMessage?: string;
  }): Promise<string | null> {
    try {
      return await this.log({
        action: params.action,
        resource: AuditResource.AUTH,
        resourceId: params.userId || 'anonymous',
        description: `${params.action} attempt by ${params.userEmail}`,
        userId: params.userId || 'anonymous',
        userEmail: params.userEmail,
        userName: params.userName || 'Unknown',
        userRole: 'UNKNOWN',
        severity: params.success ? AuditSeverity.INFO : AuditSeverity.WARNING,
        success: params.success,
        errorMessage: params.errorMessage,
      });
    } catch (error) {
      return null;
    }
  }

  /**
   * Get audit logs with filters
   */
  async getLogs(filter?: AuditLogFilter, maxResults: number = 100): Promise<AuditLog[]> {
    try {
      let q = query(collection(db, AUDIT_COLLECTION), orderBy('timestamp', 'desc'), limit(maxResults));

      if (filter) {
        if (filter.userId) {
          q = query(q, where('userId', '==', filter.userId));
        }
        if (filter.action) {
          q = query(q, where('action', '==', filter.action));
        }
        if (filter.resource) {
          q = query(q, where('resource', '==', filter.resource));
        }
        if (filter.severity) {
          q = query(q, where('severity', '==', filter.severity));
        }
        if (filter.success !== undefined) {
          q = query(q, where('success', '==', filter.success));
        }
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp.toDate(),
        } as AuditLog;
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get audit statistics
   */
  async getStats(days: number = 30): Promise<AuditLogStats> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const logs = await this.getLogs({ startDate }, 1000);

      const actionsByUser: Record<string, number> = {};
      const actionsByResource: Record<string, number> = {};
      let successfulActions = 0;
      let failedActions = 0;

      logs.forEach(log => {
        // Count by user
        actionsByUser[log.userEmail] = (actionsByUser[log.userEmail] || 0) + 1;
        
        // Count by resource
        actionsByResource[log.resource] = (actionsByResource[log.resource] || 0) + 1;
        
        // Count success/failure
        if (log.success) {
          successfulActions++;
        } else {
          failedActions++;
        }
      });

      return {
        totalLogs: logs.length,
        successfulActions,
        failedActions,
        actionsByUser,
        actionsByResource,
        recentActivity: logs.slice(0, 10),
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Helper: Log product action
   */
  async logProductAction(params: {
    action: AuditAction;
    productId: string;
    productName: string;
    user: { id: string; email: string; name: string; role: string };
    changes?: { before?: any; after?: any };
    success?: boolean;
    errorMessage?: string;
  }): Promise<string> {
    return this.log({
      action: params.action,
      resource: AuditResource.PRODUCT,
      resourceId: params.productId,
      resourceName: params.productName,
      description: `${params.action.replace('_', ' ')} - ${params.productName}`,
      userId: params.user.id,
      userEmail: params.user.email,
      userName: params.user.name,
      userRole: params.user.role,
      changes: params.changes,
      success: params.success,
      errorMessage: params.errorMessage,
    });
  }

  /**
   * Helper: Log category action
   */
  async logCategoryAction(params: {
    action: AuditAction;
    categoryId: string;
    categoryName: string;
    user: { id: string; email: string; name: string; role: string };
    changes?: { before?: any; after?: any };
    success?: boolean;
    errorMessage?: string;
  }): Promise<string> {
    return this.log({
      action: params.action,
      resource: AuditResource.CATEGORY,
      resourceId: params.categoryId,
      resourceName: params.categoryName,
      description: `${params.action.replace('_', ' ')} - ${params.categoryName}`,
      userId: params.user.id,
      userEmail: params.user.email,
      userName: params.user.name,
      userRole: params.user.role,
      changes: params.changes,
      success: params.success,
      errorMessage: params.errorMessage,
    });
  }

  /**
   * Helper: Log order action
   */
  async logOrderAction(params: {
    action: AuditAction;
    orderId: string;
    user: { id: string; email: string; name: string; role: string };
    changes?: { before?: any; after?: any };
    success?: boolean;
    errorMessage?: string;
    additionalData?: Record<string, any>;
  }): Promise<string> {
    return this.log({
      action: params.action,
      resource: AuditResource.ORDER,
      resourceId: params.orderId,
      description: `${params.action.replace('_', ' ')} - Order #${params.orderId}`,
      userId: params.user.id,
      userEmail: params.user.email,
      userName: params.user.name,
      userRole: params.user.role,
      changes: params.changes,
      success: params.success,
      errorMessage: params.errorMessage,
      additionalData: params.additionalData,
    });
  }

  /**
   * Helper: Log delivery agent action
   */
  async logDeliveryAgentAction(params: {
    action: AuditAction;
    agentId: string;
    agentName: string;
    user: { id: string; email: string; name: string; role: string };
    changes?: { before?: any; after?: any };
    success?: boolean;
    errorMessage?: string;
  }): Promise<string> {
    return this.log({
      action: params.action,
      resource: AuditResource.DELIVERY_AGENT,
      resourceId: params.agentId,
      resourceName: params.agentName,
      description: `${params.action.replace('_', ' ')} - ${params.agentName}`,
      userId: params.user.id,
      userEmail: params.user.email,
      userName: params.user.name,
      userRole: params.user.role,
      changes: params.changes,
      success: params.success,
      errorMessage: params.errorMessage,
    });
  }
}

export const auditService = new AuditService();
